/**
 * Mempool API client for the Bitcoin chain comparison tool.
 *
 * Two chains are compared:
 *  - "standard": the Bitcoin Core consensus chain, served by mempool.space
 *  - "bip110":   the BIP-110 chain, served by mempool.guide (a mempool.space fork)
 *
 * Rate-limit strategy:
 *  - Callers poll slowly (60s+ per endpoint, see hooks/useChainData.ts).
 *  - Requests are made directly first; only genuine network/CORS failures
 *    fall back to the configured CORS proxy (a different egress IP, which
 *    also helps if the visitor's IP is temporarily throttled).
 *  - HTTP errors (incl. 429) are surfaced as ApiError so TanStack Query can
 *    back off exponentially instead of hammering the API.
 */

export type ChainId = 'standard' | 'bip110';

export interface ChainConfig {
  id: ChainId;
  /** Display name of the chain. */
  name: string;
  /** Short label used in badges. */
  shortName: string;
  /** One-line description shown in the chain panel header. */
  tagline: string;
  /** Mempool API base URL (no trailing slash). */
  apiBase: string;
  /** Human-facing explorer base URL. */
  explorerBase: string;
  /** Hex accent color (used by charts, where CSS vars are awkward). */
  hex: string;
  /** Literal Tailwind class fragments for per-chain accents. */
  classes: {
    text: string;
    softBadge: string;
    panelRing: string;
    gradient: string;
    dot: string;
    progressIndicator: string;
    sparklineId: string;
  };
}

export const CHAINS: Record<ChainId, ChainConfig> = {
  standard: {
    id: 'standard',
    name: 'Bitcoin',
    shortName: 'Standard',
    tagline: 'Standard chain · Bitcoin Core consensus rules',
    apiBase: 'https://mempool.space/api',
    explorerBase: 'https://mempool.space',
    hex: '#f7931a',
    classes: {
      text: 'text-amber-600 dark:text-amber-400',
      softBadge:
        'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
      panelRing: 'border-amber-500/25',
      gradient: 'from-amber-500/12 via-transparent to-transparent',
      dot: 'bg-amber-500',
      progressIndicator: '[&_[data-slot=progress-indicator]]:bg-amber-500',
      sparklineId: 'spark-standard',
    },
  },
  bip110: {
    id: 'bip110',
    name: 'Bitcoin',
    shortName: 'BIP-110',
    tagline: 'BIP-110 chain · reduced data-carrier rules',
    apiBase: 'https://mempool.guide/api',
    explorerBase: 'https://mempool.guide',
    hex: '#8b5cf6',
    classes: {
      text: 'text-violet-600 dark:text-violet-400',
      softBadge:
        'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400',
      panelRing: 'border-violet-500/25',
      gradient: 'from-violet-500/12 via-transparent to-transparent',
      dot: 'bg-violet-500',
      progressIndicator: '[&_[data-slot=progress-indicator]]:bg-violet-500',
      sparklineId: 'spark-bip110',
    },
  },
};

export const CHAIN_LIST: ChainConfig[] = [CHAINS.standard, CHAINS.bip110];

/* ------------------------------------------------------------------ */
/* API types                                                           */
/* ------------------------------------------------------------------ */

export interface DifficultyAdjustment {
  /** Progress through the current difficulty epoch, 0-100. */
  progressPercent: number;
  /** Estimated next adjustment in percent (e.g. -2.01). */
  difficultyChange: number;
  /** Estimated retarget time, ms since epoch. */
  estimatedRetargetDate: number;
  remainingBlocks: number;
  /** Estimated time until retarget, ms. */
  remainingTime: number;
  previousRetarget: number;
  previousTime: number;
  nextRetargetHeight: number;
  /** Average block time this epoch, ms. */
  timeAvg: number;
  adjustedTimeAvg?: number;
  timeOffset: number;
  expectedBlocks?: number;
}

export interface HashratePoint {
  timestamp: number; // seconds
  avgHashrate: number; // H/s
}

export interface HashrateResponse {
  hashrates: HashratePoint[];
  difficulty?: { time: number; height: number; difficulty: number; adjustment: number }[];
  currentHashrate: number; // H/s
  currentDifficulty: number;
}

export interface MempoolInfo {
  count: number;
  vsize: number; // vbytes
  total_fee: number; // sats
  fee_histogram: [number, number][];
}

export interface RecommendedFees {
  fastestFee: number;
  halfHourFee: number;
  hourFee: number;
  economyFee: number;
  minimumFee: number;
}

export interface BlockInfo {
  id: string;
  height: number;
  timestamp: number; // seconds
  tx_count: number;
  size: number; // bytes
  weight: number;
  difficulty: number;
  extras?: {
    pool?: { id?: number | string; name?: string; slug?: string };
    medianFee?: number;
    reward?: number;
  };
}

export interface ForkStatus {
  /** The common height that was compared on both chains. */
  comparedHeight: number;
  standardHash: string;
  bip110Hash: string;
  /** true when both chains agree on the block at comparedHeight. */
  match: boolean;
}

/* ------------------------------------------------------------------ */
/* Fetch helpers                                                       */
/* ------------------------------------------------------------------ */

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';
const REQUEST_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal | undefined {
  if (typeof AbortSignal.any === 'function' && typeof AbortSignal.timeout === 'function') {
    const timeout = AbortSignal.timeout(ms);
    return signal ? AbortSignal.any([signal, timeout]) : timeout;
  }
  return signal;
}

async function rawFetch(url: string, signal?: AbortSignal): Promise<Response> {
  const res = await fetch(url, {
    signal: withTimeout(signal, REQUEST_TIMEOUT_MS),
    headers: { accept: 'application/json, text/plain' },
  });
  if (!res.ok) {
    throw new ApiError(`Request failed: HTTP ${res.status}`, res.status);
  }
  return res;
}

async function fetchWithProxyFallback<T>(
  url: string,
  parse: (res: Response) => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await parse(await rawFetch(url, signal));
  } catch (err) {
    // Respect caller cancellation and HTTP-level errors (incl. 429): those are
    // handled by the query retry/backoff logic, not by the proxy.
    if (signal?.aborted || err instanceof ApiError) throw err;
    // Network/CORS failure → retry once through the CORS proxy.
    return parse(await rawFetch(`${CORS_PROXY}${encodeURIComponent(url)}`, signal));
  }
}

export function apiJson<T>(apiBase: string, path: string, signal?: AbortSignal): Promise<T> {
  return fetchWithProxyFallback(`${apiBase}${path}`, (res) => res.json() as Promise<T>, signal);
}

export function apiText(apiBase: string, path: string, signal?: AbortSignal): Promise<string> {
  return fetchWithProxyFallback(`${apiBase}${path}`, (res) => res.text(), signal);
}

/* ------------------------------------------------------------------ */
/* Endpoint wrappers                                                   */
/* ------------------------------------------------------------------ */

export const getRecentBlocks = (apiBase: string, signal?: AbortSignal) =>
  apiJson<BlockInfo[]>(apiBase, '/blocks', signal);

export const getDifficultyAdjustment = (apiBase: string, signal?: AbortSignal) =>
  apiJson<DifficultyAdjustment>(apiBase, '/v1/difficulty-adjustment', signal);

export const getHashrate = (apiBase: string, signal?: AbortSignal) =>
  apiJson<HashrateResponse>(apiBase, '/v1/mining/hashrate/1m', signal);

export const getMempool = (apiBase: string, signal?: AbortSignal) =>
  apiJson<MempoolInfo>(apiBase, '/mempool', signal);

export const getRecommendedFees = (apiBase: string, signal?: AbortSignal) =>
  apiJson<RecommendedFees>(apiBase, '/v1/fees/recommended', signal);

export const getBlockHashAtHeight = (apiBase: string, height: number, signal?: AbortSignal) =>
  apiText(apiBase, `/block-height/${height}`, signal);

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

const intFmt = new Intl.NumberFormat('en-US');

export function formatInt(n: number): string {
  return intFmt.format(Math.round(n));
}

/** H/s → "905 EH/s" style string. */
export function formatHashrate(hps: number): string {
  if (!Number.isFinite(hps) || hps <= 0) return '—';
  const units: [number, string][] = [
    [1e21, 'ZH/s'],
    [1e18, 'EH/s'],
    [1e15, 'PH/s'],
    [1e12, 'TH/s'],
  ];
  for (const [factor, label] of units) {
    if (hps >= factor) {
      const value = hps / factor;
      return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${label}`;
    }
  }
  return `${hps.toFixed(0)} H/s`;
}

/** Raw difficulty → "127.5 T". */
export function formatDifficulty(difficulty: number): string {
  if (!Number.isFinite(difficulty) || difficulty <= 0) return '—';
  if (difficulty >= 1e12) return `${(difficulty / 1e12).toFixed(2)} T`;
  if (difficulty >= 1e9) return `${(difficulty / 1e9).toFixed(2)} G`;
  return formatInt(difficulty);
}

/** vbytes → "10.8 MvB". */
export function formatVsize(vsize: number): string {
  if (!Number.isFinite(vsize) || vsize < 0) return '—';
  if (vsize >= 1e6) return `${(vsize / 1e6).toFixed(2)} MvB`;
  if (vsize >= 1e3) return `${(vsize / 1e3).toFixed(1)} kvB`;
  return `${vsize} vB`;
}

/** bytes → "1.54 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
  return `${bytes} B`;
}

/** sats → "0.07397930 BTC". */
export function formatSatsAsBtc(sats: number): string {
  return `${(sats / 1e8).toFixed(8).replace(/\.?0+$/, '') || '0'} BTC`;
}

/** "2m 14s ago" for a unix-seconds timestamp. */
export function timeAgo(timestampSec: number, nowMs: number = Date.now()): string {
  const deltaSec = Math.max(0, Math.floor(nowMs / 1000 - timestampSec));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const minutes = Math.floor(deltaSec / 60);
  if (minutes < 60) return `${minutes}m ${deltaSec % 60}s ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h ago`;
}

/** "13d 4h" / "6h 12m" / "9m 43s" for a millisecond duration. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Average block time in ms → "9m 47s". */
export function formatBlockTime(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  return `${Math.floor(totalSec / 60)}m ${String(totalSec % 60).padStart(2, '0')}s`;
}

const dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDateTime(ms: number): string {
  return dateTimeFmt.format(new Date(ms));
}

/** Truncate a block hash for display: "00000000…0437". */
export function shortHash(hash: string, head = 8, tail = 4): string {
  if (hash.length <= head + tail + 1) return hash;
  return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}
